// src/scenes/team/EditUser.jsx
import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, isValid, parse } from "date-fns";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  getExampleNumber,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import useToast from "../../components/useToast";
import { AuthContext } from "../../AuthContext";
import { API_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { canAssignCompanyAdmin, formatAccessLevel } from "../../utils/accessLevels";
import AppButton from "../../design-system/components/AppButton";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppEditActions from "../../design-system/components/AppEditActions";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppSelect from "../../design-system/components/AppSelect";
import CountryFlag from "../../design-system/components/CountryFlag";
import AppSwitch from "../../design-system/components/AppSwitch";
import AppText from "../../design-system/components/AppText";
import AppToolbar from "../../design-system/components/AppToolbar";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { IconInfo } from "../../design-system/icons";
import { hasAppValueChanged } from "../../design-system/utils/appValueEquality";
import ExitShiftsDialog from "./ExitShiftsDialog";
import WorkingDayDefaultsEditor, {
  buildWorkingDayPayload,
  hasInvalidWorkingDayTimes,
  workingDaysFromApi,
} from "../../components/WorkingDayDefaultsEditor";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./team-edit.css";

const percentOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const weekdayOptions = [
  { value: 0, label: "Montag" },
  { value: 1, label: "Dienstag" },
  { value: 2, label: "Mittwoch" },
  { value: 3, label: "Donnerstag" },
  { value: 4, label: "Freitag" },
  { value: 5, label: "Samstag" },
  { value: 6, label: "Sonntag" },
];

const e164ToWhatsApp00 = (e164) => (e164 ? e164.replace(/^\+/, "00") : null);

// Vergleichbare Form fuer den Dirty-Check: nach Wochentag sortiert, Zeiten von
// Ganztags-Tagen geleert — so meldet der Vergleich keine Scheindifferenz.
const comparableWorkingDays = (workingDays) =>
  [...(workingDays || [])]
    .sort((left, right) => left.weekday - right.weekday)
    .map((day) => ({
      weekday: day.weekday,
      isFullDay: Boolean(day.isFullDay),
      startTime: day.isFullDay ? "" : day.startTime || "",
      endTime: day.isFullDay ? "" : day.endTime || "",
    }));

const fullDayWorkingDays = (weekdays) =>
  weekdays.map((weekday) => ({ weekday, isFullDay: true, startTime: "", endTime: "" }));

const formatDateForDisplay = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(rawValue)) return rawValue;

  const isoValue = rawValue.slice(0, 10);
  const parsedDate = parse(isoValue, "yyyy-MM-dd", new Date());
  return isValid(parsedDate) && format(parsedDate, "yyyy-MM-dd") === isoValue
    ? format(parsedDate, "dd.MM.yyyy")
    : rawValue;
};

const formatDateForApi = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parsedDate = parse(rawValue, "dd.MM.yyyy", new Date());
  return isValid(parsedDate) && format(parsedDate, "dd.MM.yyyy") === rawValue
    ? format(parsedDate, "yyyy-MM-dd")
    : null;
};

const backend00ToE164 = (v) => {
  if (!v) return "";
  const s = String(v).trim().replace(/\s+/g, "");
  return s.startsWith("00") ? `+${s.slice(2)}` : s;
};

const buildPhoneHint = (country = "CH") => {
  try {
    const ex = getExampleNumber(country, examples);
    if (!ex) {
      const cc = getCountryCallingCode(country);
      return { placeholder: `+${cc} ...`, hint: `+${cc} ...` };
    }
    return {
      // Im Feld steht bewusst nur die nationale Schreibweise (079 123 45 67):
      // kurz, vertraut und ohne zusaetzlichen Erklaertext daneben.
      placeholder: ex.formatNational(),
      hint: `${ex.formatInternational()}  /  ${ex.formatNational()}`,
    };
  } catch {
    const cc = getCountryCallingCode(country);
    return { placeholder: `+${cc} ...`, hint: `+${cc} ...` };
  }
};

const hasOnlyAllowedPhoneChars = (val) => {
  if (!val) return true;
  if (!/^[0-9+().\-\s]*$/.test(val)) return false;

  const plusCount = (val.match(/\+/g) || []).length;
  if (plusCount > 1) return false;
  if (plusCount === 1 && !val.trim().startsWith("+")) return false;

  return true;
};

const normalizeToE164 = (raw, defaultCountry = "CH") => {
  if (!raw) return null;

  let v = String(raw).trim();
  if (!hasOnlyAllowedPhoneChars(v)) return null;

  v = v.replace(/[^\d+]/g, "");
  if (v.startsWith("00")) v = `+${v.slice(2)}`;

  try {
    const p = parsePhoneNumberFromString(v, defaultCountry);
    if (!p || !p.isValid()) return null;
    return p.number;
  } catch {
    return null;
  }
};

const inferCountryFromE164 = (e164, fallback = "CH") => {
  if (!e164) return fallback;
  try {
    const p = parsePhoneNumberFromString(e164);
    return p?.country || fallback;
  } catch {
    return fallback;
  }
};

const backendPhoneToForm = (backendPhone, fallbackCountry = "CH") => {
  const e164 = backend00ToE164(backendPhone || "");
  const country = inferCountryFromE164(e164, fallbackCountry);

  try {
    const p = e164 ? parsePhoneNumberFromString(e164, country) : null;
    return {
      phone_country: country,
      phone_number: p ? p.formatInternational() : e164,
    };
  } catch {
    return { phone_country: fallbackCountry, phone_number: e164 };
  }
};

const DetailGrid = ({ children }) => (
  <AppFieldGrid columns={2} className="team-edit-detail-grid">
    {children}
  </AppFieldGrid>
);

const DetailItem = ({ label, value }) => {
  const isTextValue = typeof value === "string" || typeof value === "number";

  return (
    <div className="team-edit-detail">
      <AppText variant="body-3" tone="secondary" className="team-edit-detail__label">
        {label}
      </AppText>
      {isTextValue ? (
        <AppText variant="body-1" tone="primary" truncate title={typeof value === "string" ? value : undefined}>
          {value || "-"}
        </AppText>
      ) : (
        value || "-"
      )}
    </div>
  );
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const sortedNumbers = (values = []) => values.map(Number).sort((a, b) => a - b);

const cardState = (state, card) => {
  if (!state) return null;
  const stateForm = state.form || {};

  if (card === "personal") {
    return {
      first_name: stateForm.first_name || "",
      last_name: stateForm.last_name || "",
      email: stateForm.email || "",
      phone_country: stateForm.phone_country || "CH",
      phone_number: stateForm.phone_number || "",
      entry_date: stateForm.entry_date || "",
      exit_date: stateForm.exit_date || "",
    };
  }
  if (card === "employment") {
    return {
      employment: stateForm.employment || "",
      employment_level_pct: String(stateForm.employment_level_pct ?? ""),
      hourly_wage: String(stateForm.hourly_wage ?? ""),
      experience: String(stateForm.experience ?? ""),
    };
  }
  if (card === "settings") {
    return {
      access_level: stateForm.access_level || "",
      availability_mode: stateForm.availability_mode || "",
      in_training: Boolean(stateForm.in_training),
      regular_user: Boolean(stateForm.regular_user),
      workingDays: comparableWorkingDays(state.workingDays),
    };
  }
  if (card === "assignments") {
    const selectedDepartmentIds = sortedNumbers(state.selectedDepartmentIds);
    return {
      selectedLocationIds: sortedNumbers(state.selectedLocationIds),
      selectedDepartmentIds,
      rankingByDepartmentId: Object.fromEntries(
        selectedDepartmentIds.map((departmentId) => [
          String(departmentId),
          String(state.rankingByDepartmentId?.[String(departmentId)] ?? ""),
        ])
      ),
    };
  }
  if (card === "collaboration") {
    return {
      preferredEmployeeIds: sortedNumbers(state.preferredEmployeeIds),
      avoidableEmployeeIds: sortedNumbers(state.avoidableEmployeeIds),
    };
  }
  return null;
};

const SectionCard = ({
  title,
  icon,
  editing,
  editDisabled,
  onEdit,
  onCancel,
  onSave,
  saving,
  dirty,
  children,
}) => {
  return (
    <AppCard className="team-edit-card">
      <AppCardHeader
        title={title}
        visual={icon}
        action={editing ? (
          <div className="team-edit-card__header-actions">
            <AppEditActions
              editing
              dirty={dirty}
              saving={saving}
              onCancel={onCancel}
              onSave={onSave}
            />
          </div>
        ) : onEdit ? (
          <AppEditActions
            onEdit={onEdit}
            editDisabled={editDisabled}
            editLabel={`${title} bearbeiten`}
          />
        ) : null}
      />
      <AppCardBody>{children}</AppCardBody>
    </AppCard>
  );
};

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedLocationId } = useContext(AuthContext);
  const { success, error } = useToast();
  const token = localStorage.getItem("session_token");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const isSavingRef = useRef(false);
  const [users, setUsers] = useState([]);
  const [exitDialog, setExitDialog] = useState({ open: false, impact: null, payload: null });
  const [viewerAccessLevel, setViewerAccessLevel] = useState("");

  // Form state
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_country: "CH",
    phone_number: "",
    entry_date: "",
    exit_date: "",
    employment: "Perm", // Perm | Temp
    employment_level_pct: 100, // show as percent 10..100
    hourly_wage: "",
    experience: "",
    in_training: false,
    regular_user: true,
    availability_mode: "absence",
    access_level: "User",
  });

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [rankingByDepartmentId, setRankingByDepartmentId] = useState({});
  const [locations, setLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  // Der Standard-Standort ist der Einstiegsstandort dieser Person in Web und
  // App. Er wird ausdruecklich gesetzt, nicht aus der Reihenfolge der
  // Standortauswahl abgeleitet.
  const [primaryLocationId, setPrimaryLocationId] = useState("");
  const [preferredEmployeeIds, setPreferredEmployeeIds] = useState([]);
  const [avoidableEmployeeIds, setAvoidableEmployeeIds] = useState([]);
  const [workingDays, setWorkingDays] = useState([]);
  const [workingDaysConfigured, setWorkingDaysConfigured] = useState(false);
  // Arbeitstage koennen nur Tage sein, an denen der Standort offen ist — dieselbe
  // Menge, die die Verfuegbarkeits-Uebersicht im Modus "Abwesenheit" vorbelegt.
  const [openingWeekdays, setOpeningWeekdays] = useState([]);
  const countryOptions = useMemo(
    () =>
      getCountries().map((code) => ({
        code,
        callingCode: getCountryCallingCode(code),
      })),
    []
  );

  const currentUserState = {
    form,
    selectedDepartmentIds,
    rankingByDepartmentId,
    selectedLocationIds,
    preferredEmployeeIds,
    avoidableEmployeeIds,
    workingDays,
    workingDaysConfigured,
  };
  const isCardDirty = (card) =>
    hasAppValueChanged(cardState(snapshot, card), cardState(currentUserState, card));

  // Sichtbar sind die Abteilungen aller Standorte, die dieser Person zugeordnet
  // sind. Ohne Zuordnung faellt es auf den aktuell aktiven Standort zurueck.
  const activeLocationIds = useMemo(() => {
    const assigned = (selectedLocationIds || []).map(Number).filter(Boolean);
    if (assigned.length) return assigned;
    const fallback = Number(selectedLocationId || locations[0]?.id);
    return fallback ? [fallback] : [];
  }, [locations, selectedLocationId, selectedLocationIds]);

  // Der Standard muss immer einer der zugeordneten Standorte sein — wird er
  // abgewaehlt, rutscht er auf den ersten verbleibenden.
  const effectivePrimaryLocationId = useMemo(() => {
    const assigned = (selectedLocationIds || []).map(Number).filter(Boolean);
    if (!assigned.length) return "";
    return assigned.includes(Number(primaryLocationId)) ? Number(primaryLocationId) : assigned[0];
  }, [primaryLocationId, selectedLocationIds]);

  const primaryLocationOptions = useMemo(
    () =>
      (selectedLocationIds || [])
        .map((locationId) => locations.find((item) => Number(item.id) === Number(locationId)))
        .filter(Boolean)
        .map((item) => ({ value: Number(item.id), label: item.name })),
    [locations, selectedLocationIds]
  );

  const visibleDepartmentOptions = useMemo(() => {
    if (!activeLocationIds.length) return [];
    const allowed = new Set(activeLocationIds);
    return departmentOptions.filter((department) => allowed.has(Number(department.location_id)));
  }, [activeLocationIds, departmentOptions]);

  // Bei mehreren Standorten wird das Dropdown nach Standort gruppiert, damit
  // gleichnamige Abteilungen unterscheidbar bleiben.
  const departmentSelectOptions = useMemo(() => {
    const toOption = (department) => ({
      value: Number(department.id),
      label: department.name,
      displayLabel: department.name,
    });

    if (activeLocationIds.length <= 1) {
      return visibleDepartmentOptions.map(toOption);
    }

    return activeLocationIds
      .map((locationId) => {
        const locationName = locations.find((location) => Number(location.id) === locationId)?.name
          || "Ohne Standort";
        const options = visibleDepartmentOptions
          .filter((department) => Number(department.location_id) === locationId)
          .map(toOption);
        return options.length ? { key: String(locationId), label: locationName, options } : null;
      })
      .filter(Boolean);
  }, [activeLocationIds, locations, visibleDepartmentOptions]);

  const departmentLocationName = (department) =>
    locations.find((location) => Number(location.id) === Number(department.location_id))?.name || "";

  const currentUserIndex = useMemo(
    () => users.findIndex((u) => String(u.id) === String(id)),
    [users, id]
  );

  const currentUserOptionValue =
    currentUserIndex >= 0 ? String(users[currentUserIndex].id) : "";

  const employeeOptions = useMemo(
    () => users.filter((teamUser) => String(teamUser.id) !== String(id)),
    [users, id]
  );

  const getUserLabel = (teamUser) => {
    if (!teamUser) return "";

    const fullName = [teamUser.first_name, teamUser.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullName || teamUser.email || String(teamUser.id);
  };

  const createUserState = (u) => {
    const formPhone = backendPhoneToForm(u.phone_number || u.phone, "CH");
    const availabilityMode = u.availability_mode || "absence";
    const workingDaysAreConfigured = Boolean(u.working_days_configured);
    const userOpeningWeekdays = Array.isArray(u.opening_weekdays) && u.opening_weekdays.length
      ? u.opening_weekdays.map(Number)
      : weekdayOptions.map((option) => option.value);
    const storedWorkingDays = workingDaysFromApi(u.working_day_defaults).length
      ? workingDaysFromApi(u.working_day_defaults)
      : fullDayWorkingDays((u.working_day_weekdays || []).map(Number));
    return {
      form: {
        first_name: u.first_name || "",
        last_name: u.last_name || "",
        email: u.email || "",
        phone_country: formPhone.phone_country,
        phone_number: formPhone.phone_number,
        entry_date: formatDateForDisplay(u.entry_date),
        exit_date: formatDateForDisplay(u.exit_date),
        employment: u.employment || "Perm",
        employment_level_pct: u.employment_level
          ? Math.round(Number(u.employment_level) * 100)
          : 100,
        hourly_wage: u.hourly_wage || "",
        experience: u.experience || "",
        in_training: u.in_training === "X" || u.in_training === true,
        regular_user: u.regular_user === "X" || u.regular_user === true,
        availability_mode: availabilityMode,
        access_level: u.access_level || "User",
      },
      selectedDepartmentIds: (u.selected_department_ids || []).map(Number),
      rankingByDepartmentId: Object.fromEntries(
        Object.entries(u.ranking_by_department_id || {}).map(([departmentId, ranking]) => [
          String(departmentId),
          ranking ?? "",
        ])
      ),
      selectedLocationIds: (u.selected_location_ids || []).map(Number),
      primaryLocationId: u.primary_location_id ? Number(u.primary_location_id) : "",
      preferredEmployeeIds: (u.preferred_employee_ids || []).map(Number),
      avoidableEmployeeIds: (u.avoidable_employee_ids || []).map(Number),
      workingDays: workingDaysAreConfigured
        ? storedWorkingDays
        : availabilityMode === "absence"
          ? fullDayWorkingDays(userOpeningWeekdays)
          : [],
      workingDaysConfigured: workingDaysAreConfigured,
      openingWeekdays: userOpeningWeekdays,
    };
  };

  const applyUserState = (state, { persistSnapshot = false } = {}) => {
    if (!state) return;
    setForm(state.form);
    setSelectedDepartmentIds(state.selectedDepartmentIds);
    setRankingByDepartmentId(state.rankingByDepartmentId);
    setSelectedLocationIds(state.selectedLocationIds);
    setPrimaryLocationId(state.primaryLocationId ?? "");
    setPreferredEmployeeIds(state.preferredEmployeeIds);
    setAvoidableEmployeeIds(state.avoidableEmployeeIds);
    setWorkingDays(state.workingDays);
    setWorkingDaysConfigured(Boolean(state.workingDaysConfigured));
    setOpeningWeekdays(state.openingWeekdays || []);
    if (persistSnapshot) {
      setSnapshot(state);
    }
  };

  const startEditing = (card) => {
    if (snapshot) {
      applyUserState(snapshot);
    }
    setEditingCard(card);
  };

  const cancelEditing = () => {
    if (snapshot) {
      applyUserState(snapshot);
    }
    setEditingCard(null);
  };

  const showNotice = (severity, message) => {
    if (severity === "success") {
      success(message);
      return;
    }
    error(message);
  };

  const navigateToUser = (targetId) => {
    if (!targetId || String(targetId) === String(id)) return;
    navigate(`/team/${targetId}/edit`);
  };

  const handlePreviousUser = () => {
    if (currentUserIndex <= 0) return;
    navigateToUser(users[currentUserIndex - 1]?.id);
  };

  const handleNextUser = () => {
    if (currentUserIndex < 0 || currentUserIndex >= users.length - 1) return;
    navigateToUser(users[currentUserIndex + 1]?.id);
  };


  // Load the team list and find the selected user locally.
  const loadUser = async ({ showLoader = true } = {}) => {
    if (showLoader) {
      setIsLoading(true);
    }
    const list = await axios.get(`${API_BASE_URL}/api/web/team/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const teamUsers = list.data.users || [];
    setUsers(teamUsers);
    setViewerAccessLevel(list.data.access_level || "");
    setDepartmentOptions(list.data.department_options || []);
    setLocations(list.data.locations || []);
    const found = teamUsers.find((u) => String(u.id) === String(id));
    if (!found) throw new Error("User not found");
    return found;
  };

  // User laden + Basis-Form füllen + initiale Departments setzen
  useEffect(() => {
    (async () => {
      try {
        const u = await loadUser();
        applyUserState(createUserState(u), { persistSnapshot: true });
        setEditingCard(null);
      } catch (e) {
        showNotice("error", t("team.load_user_failed"));
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onChange = (field) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const onHourlyWageChange = (event) => {
    const value = String(event.target.value || "").replace(",", ".");
    if (value === "" || /^\d+(?:\.\d{0,2})?$/.test(value)) {
      setForm((current) => ({ ...current, hourly_wage: value }));
    }
  };

  const buildCardPayload = (card) => {
    if (isSavingRef.current) return;

    if (card === "personal") {
      const first = (form.first_name || "").trim();
      const last = (form.last_name || "").trim();
      const mail = (form.email || "").trim();

      if (!first || !last || !mail) {
        showNotice("error", t("registration.required"));
        return null;
      }
      if (!mail.includes("@")) {
        showNotice("error", t("registration.invalid_email"));
        return null;
      }
      const rawPhone = (form.phone_number || "").trim();
      const phoneE164 = normalizeToE164(rawPhone, form.phone_country || "CH");
      if (rawPhone && !phoneE164) {
        showNotice("error", t("registration.phone_number_invalid"));
        return null;
      }

      const payload = {
        first_name: first,
        last_name: last,
        email: mail,
        phone_number: phoneE164 ? e164ToWhatsApp00(phoneE164) : "",
      };
      const hasStoredDates = Boolean(snapshot?.form?.entry_date || snapshot?.form?.exit_date);
      if (hasStoredDates) {
        const entryDate = formatDateForApi(form.entry_date);
        const exitDate = formatDateForApi(form.exit_date);
        if (entryDate === null || exitDate === null) {
          showNotice("error", "Bitte Datum im Format dd.mm.yyyy eingeben.");
          return null;
        }
        if (!entryDate) {
          showNotice("error", t("registration.required"));
          return null;
        }
        if (entryDate && exitDate && exitDate < entryDate) {
          showNotice("error", "Das Austrittsdatum darf nicht vor dem Eintrittsdatum liegen.");
          return null;
        }
        payload.entry_date = entryDate || null;
        payload.exit_date = exitDate || null;
      }
      return payload;
    }

    if (card === "employment") {
      if (!form.employment || !Number(form.employment_level_pct)) {
        showNotice("error", t("registration.required"));
        return null;
      }
      const hourlyWage = String(form.hourly_wage ?? "").trim();
      if (hourlyWage && (!Number.isFinite(Number(hourlyWage)) || Number(hourlyWage) < 0)) {
        showNotice("error", t("registration.invalid"));
        return null;
      }
      return {
        employment: form.employment,
        employment_level: Number(form.employment_level_pct),
        hourly_wage: hourlyWage ? Number(hourlyWage) : "",
        experience: form.experience || null,
      };
    }

    if (card === "settings") {
      if (!form.access_level || !form.availability_mode) {
        showNotice("error", t("registration.required"));
        return null;
      }
      if (hasInvalidWorkingDayTimes(workingDays)) {
        showNotice("error", t("working_day_defaults.invalid_times", {
          defaultValue: "Bitte gib bei Arbeitstagen mit Zeiten ein gültiges Von–Bis an.",
        }));
        return null;
      }
      return {
        in_training: form.in_training,
        regular_user: form.regular_user,
        availability_mode: form.availability_mode,
        access_level: form.access_level,
        // Nur mitschicken, was wirklich hinterlegt ist. Ohne eigene Arbeitstage
        // zeigt das Formular die Rueckfallebene des Modus an — wuerde sie beim
        // Speichern mitgeschrieben, waeren daraus echte Arbeitstage, und die
        // schlagen ab dann den Verfuegbarkeitsmodus.
        ...(workingDaysConfigured
          ? { working_day_defaults: buildWorkingDayPayload(workingDays) }
          : {}),
      };
    }

    if (card === "assignments") {
      if (
        (canManageUserLocations && locations.length > 1 && selectedLocationIds.length === 0)
        || selectedDepartmentIds.length === 0
      ) {
        showNotice("error", t("registration.required"));
        return null;
      }
      const selectedDepartmentSet = new Set(selectedDepartmentIds.map(Number));
      const selectedDepartmentRanking = Object.fromEntries(
        Object.entries(rankingByDepartmentId).filter(([departmentId]) =>
          selectedDepartmentSet.has(Number(departmentId))
        )
      );
      return {
        selected_department_ids: selectedDepartmentIds,
        ranking_by_department_id: selectedDepartmentRanking,
        ...(canManageUserLocations
          ? {
            selected_location_ids: selectedLocationIds,
            primary_location_id: effectivePrimaryLocationId || null,
          }
          : {}),
      };
    }

    if (card === "collaboration") {
      return {
        preferred_employee_ids: preferredEmployeeIds,
        avoidable_employee_ids: avoidableEmployeeIds,
      };
    }

    return null;
  };

  const storedExitDateApi = formatDateForApi(snapshot?.form?.exit_date || "") || null;

  // Meldet, wie viele Schichten nach dem neuen Austrittsdatum offen blieben.
  // Faellt der Aufruf aus, greift die 409-Antwort des Update-Endpunkts.
  const loadExitImpact = async (exitDateApi) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/web/team/users/${id}/exit-impact`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { exit_date: exitDateApi },
        }
      );
      return response.data || null;
    } catch {
      return null;
    }
  };

  const persistCard = async (card, payload) => {
    try {
      isSavingRef.current = true;
      setIsSaving(true);
      const response = await axios.put(
        `${API_BASE_URL}/api/web/team/users/update/${id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const refreshedUser = await loadUser({ showLoader: false });
      applyUserState(createUserState(refreshedUser), { persistSnapshot: true });
      setEditingCard(null);
      const resolvedShifts = response?.data?.exit_shifts;
      if (resolvedShifts?.shift_count) {
        showNotice(
          "success",
          resolvedShifts.mode === "delete"
            ? `${resolvedShifts.shift_count} Schichten nach dem Austritt wurden gelöscht.`
            : `${resolvedShifts.shift_count} Schichten nach dem Austritt wurden übertragen.`
        );
      } else {
        showNotice("success", t("notification.success_update"));
      }
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.reason === "EMAIL_EXISTS") {
        showNotice("error", t("registration.email_exist"));
      } else if (e?.response?.status === 409 && e?.response?.data?.reason === "PHONE_EXISTS") {
        showNotice("error", t("registration.phone_exist"));
      } else if (e?.response?.status === 409 && e?.response?.data?.reason === "EXIT_SHIFTS_PENDING") {
        // Zwischen Vorschau und Speichern kann sich der Plan geaendert haben.
        setExitDialog({ open: true, impact: e.response.data, payload });
      } else {
        showNotice("error", t("notification.no_success_update"));
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSaveCard = async (card) => {
    if (isSavingRef.current || !isCardDirty(card)) return;
    const payload = buildCardPayload(card);
    if (!payload) return;

    // Neues oder verschobenes Austrittsdatum: erst klaeren, was mit den
    // Schichten danach passiert, dann speichern.
    const nextExitDate = payload.exit_date || null;
    if (nextExitDate && nextExitDate !== storedExitDateApi) {
      const impact = await loadExitImpact(nextExitDate);
      if (impact?.shift_count) {
        setExitDialog({ open: true, impact, payload });
        return;
      }
    }

    await persistCard(card, payload);
  };

  const closeExitDialog = () => setExitDialog({ open: false, impact: null, payload: null });

  const handleExitDialogConfirm = async (action) => {
    const pendingPayload = exitDialog.payload;
    closeExitDialog();
    if (!pendingPayload) return;
    await persistCard("personal", { ...pendingPayload, exit_shift_action: action });
  };

  const phoneCountry = form.phone_country || "CH";
  // Nur ein schlichtes Beispiel im Feld – kein zusaetzlicher Hinweistext.
  const { placeholder } = buildPhoneHint(phoneCountry);
  const selectedCountry =
    countryOptions.find((o) => o.code === phoneCountry) ||
    countryOptions.find((o) => o.code === "CH") ||
    null;

  const accessLevelOptions = useMemo(() => {
    const options = ["User", "Admin"];
    if (
      canAssignCompanyAdmin(viewerAccessLevel) ||
      form.access_level === "company_admin"
    ) {
      options.push("company_admin");
    }
    return options;
  }, [form.access_level, viewerAccessLevel]);

  const experienceOptions = useMemo(
    () => [1, 2, 3, 4, 5].map((value) => ({
      value: String(value),
      label: t(`team.experience_levels.${value}`),
    })),
    [t]
  );
  const departmentPreferenceOptions = useMemo(
    () => [1, 2, 3, 4, 5].map((value) => ({
      value,
      label: t(`team.department_preference_levels.${value}`),
    })),
    [t]
  );

  const canManageUserLocations = canAssignCompanyAdmin(viewerAccessLevel);

  const showEmploymentDateFields = Boolean(
    snapshot?.form?.entry_date ||
      snapshot?.form?.exit_date ||
      form.entry_date ||
      form.exit_date
  );

  const formatEmployment = (value) => {
    if (value === "Perm") return t("registration.full_time") || "Festanstellung";
    if (value === "Temp") return t("registration.part_time") || "Anstellung auf Stundenbasis";
    return displayValue(value);
  };

  const formatBoolean = (value) => (value ? "Ja" : "Nein");

  const formatAvailabilityMode = (value) =>
    value === "availability"
      ? "Verfügbarkeiten erfassen"
      : "Abwesenheiten erfassen";
  const formatExperience = (value) =>
    experienceOptions.find((option) => String(option.value) === String(value))?.label
    || displayValue(value);
  const formatDepartmentPreference = (value) =>
    departmentPreferenceOptions.find((option) => Number(option.value) === Number(value))?.label
    || displayValue(value);

  const selectedLocationNames =
    locations
      .filter((location) => selectedLocationIds.map(Number).includes(Number(location.id)))
      .map((location) => location.name)
      .join(", ") || "Keine Standorte";

  const primaryLocationName =
    locations.find((location) => Number(location.id) === Number(effectivePrimaryLocationId))?.name
    || "Kein Standard-Standort";

  const selectedDepartments =
    visibleDepartmentOptions
      .filter((department) => selectedDepartmentIds.map(Number).includes(Number(department.id)));

  const selectedDepartmentSummary = selectedDepartments.length
    ? selectedDepartments
      .map((department) => {
        const preference = rankingByDepartmentId[String(department.id)];
        return preference
          ? `${department.name} · ${formatDepartmentPreference(preference)}`
          : department.name;
      })
      .join(", ")
    : "Keine Abteilungen";

  const preferredEmployeeNames =
    employeeOptions
      .filter((employee) => preferredEmployeeIds.map(Number).includes(Number(employee.id)))
      .map((employee) => getUserLabel(employee))
      .join(", ") || "Keine bevorzugten Mitarbeitenden";

  const avoidableEmployeeNames =
    employeeOptions
      .filter((employee) => avoidableEmployeeIds.map(Number).includes(Number(employee.id)))
      .map((employee) => getUserLabel(employee))
      .join(", ") || "Keine Einschränkungen";

  const formatWorkingDaysSelection = (days) => {
    const sortedDays = comparableWorkingDays(days);
    if (sortedDays.length === 0) {
      return t("team.working_days_never_available");
    }
    // "Immer verfügbar" heisst: an jedem Tag, an dem der Standort offen ist.
    const alwaysAvailableCount = openingWeekdays.length || weekdayOptions.length;
    if (sortedDays.length === alwaysAvailableCount && sortedDays.every((day) => day.isFullDay)) {
      return t("team.working_days_always_available");
    }
    return sortedDays
      .map((day) => {
        const weekdayLabel = weekdayOptions.find((option) => option.value === day.weekday)?.label || "";
        return day.isFullDay ? weekdayLabel : `${weekdayLabel} ${day.startTime}–${day.endTime}`;
      })
      .join(", ");
  };

  const workingDaysLabel = formatWorkingDaysSelection(workingDays);

  const anyCardEditing = Boolean(editingCard);

  return (
    <div className="team-edit-page">
      <AppWorkspace title={t("team.edit_title") || "Edit Team Member"} loading={isLoading}>
        <AppToolbar
          className="team-edit-toolbar"
          left={(
            <>
              <AppTooltip content={t("team.previous_user") || "Vorherige Person"}>
                <AppButton
                  variant="neutral"
                  size="sm"
                  iconOnly
                  aria-label={t("team.previous_user") || "Vorherige Person"}
                  onClick={handlePreviousUser}
                  disabled={currentUserIndex <= 0}
                >
                  <NavigateBeforeIcon />
                </AppButton>
              </AppTooltip>
              <div className="team-edit-toolbar__user-select">
                <AppSelect
                  size="md"
                  value={currentUserOptionValue}
                  onChange={(event) => navigateToUser(event.target.value)}
                  disabled={users.length === 0}
                  searchable
                  searchPlaceholder={t("team.select_user") || "Person suchen"}
                  placeholder={t("team.select_user") || "Person auswählen"}
                  options={users.map((teamUser) => ({
                    value: String(teamUser.id),
                    label: getUserLabel(teamUser),
                  }))}
                  aria-label={t("team.select_user") || "Person auswählen"}
                />
              </div>
              <AppTooltip content={t("team.next_user") || "Nächste Person"}>
                <AppButton
                  variant="neutral"
                  size="sm"
                  iconOnly
                  aria-label={t("team.next_user") || "Nächste Person"}
                  onClick={handleNextUser}
                  disabled={currentUserIndex < 0 || currentUserIndex >= users.length - 1}
                >
                  <NavigateNextIcon />
                </AppButton>
              </AppTooltip>
            </>
          )}
        />

        {/* AppWorkspace deckt nur das allererste Laden ab. Beim Personenwechsel
            über die Toolbar bleibt die Seite stehen — dann verschwinden die
            Karten und an ihrer Stelle steht das DS-Ladesymbol, wie beim
            Erstaufbau: Titel und Toolbar bleiben, dahinter ist nichts zu sehen. */}
        {isLoading ? (
          <div className="team-edit-loading">
            <AppLoadingOverlay open scope="contained" label="Laden…" />
          </div>
        ) : (
        <div className="team-edit-card-grid">
        <SectionCard
          title={t("team.edit_personal") || "Persönliche Daten"}
          icon={<PersonOutlineOutlinedIcon />}
          editing={editingCard === "personal"}
          editDisabled={anyCardEditing}
          onEdit={() => startEditing("personal")}
          onCancel={cancelEditing}
          onSave={() => handleSaveCard("personal")}
          saving={isSaving && editingCard === "personal"}
          dirty={isCardDirty("personal")}
        >
          {editingCard === "personal" ? (
            <AppFieldGrid columns={2} size="md">
              <AppField label={t("team.columns.first_name")} required>
                <AppInput value={form.first_name} onChange={onChange("first_name")} />
              </AppField>
              <AppField label={t("team.columns.last_name")} required>
                <AppInput value={form.last_name} onChange={onChange("last_name")} />
              </AppField>
              <AppField label={t("team.columns.email")} required>
                <AppInput type="email" value={form.email} onChange={onChange("email")} />
              </AppField>
              <AppFieldGrid columns={2} size="md" className="team-edit-phone-fields">
                <AppField label={t("registration.country") || "Land"}>
                  <AppSelect
                    searchable
                    value={selectedCountry?.code || "CH"}
                    onChange={(event) => setForm((current) => ({ ...current, phone_country: event.target.value || "CH" }))}
                    options={countryOptions.map((option) => ({
                      value: option.code,
                      label: `${option.code} +${option.callingCode}`,
                      displayLabel: `+${option.callingCode}`,
                      leadingVisual: <CountryFlag countryCode={option.code} />,
                    }))}
                  />
                </AppField>
                <AppField label={t("registration.phone_number")}>
                  <AppInput
                    value={form.phone_number}
                    onChange={onChange("phone_number")}
                    onBlur={() => {
                      const e164 = normalizeToE164(form.phone_number, form.phone_country || "CH");
                      if (!e164) return;
                      try {
                        const parsedPhone = parsePhoneNumberFromString(e164);
                        setForm((current) => ({
                          ...current,
                          phone_number: parsedPhone ? parsedPhone.formatInternational() : current.phone_number,
                        }));
                      } catch {
                        // Keep the entered value when formatting fails.
                      }
                    }}
                    placeholder={placeholder}
                  />
                </AppField>
              </AppFieldGrid>
              {showEmploymentDateFields ? (
                <AppField label="Eintrittsdatum" required>
                  <AppInput
                    value={form.entry_date}
                    onChange={onChange("entry_date")}
                    placeholder="dd.mm.yyyy"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </AppField>
              ) : null}
              {showEmploymentDateFields ? (
                <AppField label="Austrittsdatum">
                  <AppInput
                    value={form.exit_date}
                    onChange={onChange("exit_date")}
                    placeholder="dd.mm.yyyy"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </AppField>
              ) : null}
            </AppFieldGrid>
          ) : (
            <DetailGrid>
              <DetailItem label={t("team.columns.first_name")} value={displayValue(form.first_name)} />
              <DetailItem label={t("team.columns.last_name")} value={displayValue(form.last_name)} />
              <DetailItem label={t("team.columns.email")} value={displayValue(form.email)} />
              <DetailItem label={t("registration.phone_number")} value={displayValue(form.phone_number)} />
              {showEmploymentDateFields ? (
                <DetailItem label="Eintrittsdatum" value={displayValue(form.entry_date)} />
              ) : null}
              {showEmploymentDateFields ? (
                <DetailItem label="Austrittsdatum" value={displayValue(form.exit_date)} />
              ) : null}
            </DetailGrid>
          )}
        </SectionCard>

        <SectionCard
          title={t("team.edit_user_settings") || "Nutzereinstellungen"}
          icon={<SettingsOutlinedIcon />}
          editing={editingCard === "settings"}
          editDisabled={anyCardEditing}
          onEdit={() => startEditing("settings")}
          onCancel={cancelEditing}
          onSave={() => handleSaveCard("settings")}
          saving={isSaving && editingCard === "settings"}
          dirty={isCardDirty("settings")}
        >
          {editingCard === "settings" ? (
            <AppFieldGrid columns={2} size="md">
              <AppField
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.columns.access_level")}
                    <AppTooltip
                      content={t(
                        accessLevelOptions.includes("company_admin")
                          ? "registration.access_level_info_with_key_user"
                          : "registration.access_level_info"
                      )}
                    >
                      <IconInfo aria-label={t("registration.access_level_info_aria")} />
                    </AppTooltip>
                  </span>
                )}
                required
              >
                <AppSelect value={form.access_level} onChange={onChange("access_level")} options={accessLevelOptions.map((option) => ({ value: option, label: formatAccessLevel(option) }))} />
              </AppField>
              <AppField
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("availability.user_settings.mode_title")}
                    <AppTooltip content={t("availability.user_settings.mode_info")}>
                      <IconInfo aria-label={t("availability.user_settings.mode_info_aria")} />
                    </AppTooltip>
                  </span>
                )}
                required
              >
                <AppSelect
                  value={form.availability_mode}
                  onChange={(event) => {
                    const nextMode = event.target.value;
                    setForm((current) => ({
                      ...current,
                      availability_mode: nextMode,
                    }));
                    // Der Modus setzt die Arbeitstage neu: "Abwesenheit" heisst
                    // standardmaessig an allen Oeffnungstagen verfuegbar,
                    // "Verfuegbarkeit" an keinem. Einzelne Tage und Zeiten
                    // koennen danach von Hand angepasst werden.
                    setWorkingDays(
                      nextMode === "absence" ? fullDayWorkingDays(openingWeekdays) : []
                    );
                    setWorkingDaysConfigured(true);
                  }}
                  options={[
                    {
                      value: "absence",
                      label: t("availability.user_settings.absence"),
                    },
                    {
                      value: "availability",
                      label: t("availability.user_settings.availability"),
                    },
                  ]}
                />
              </AppField>
              <AppSwitch
                label={t("team.columns.in_training")}
                description={t("team.tooltips.in_training")}
                checked={Boolean(form.in_training)}
                onChange={onChange("in_training")}
              />
              <AppSwitch
                label={t("team.columns.regular_user")}
                description={t("team.tooltips.manual_planning")}
                checked={Boolean(form.regular_user)}
                onChange={onChange("regular_user")}
              />
              <AppField
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.working_days_standard")}
                    <AppTooltip content={t("team.tooltips.working_days_standard")}>
                      <IconInfo aria-label={t("team.tooltips.working_days_standard_aria")} />
                    </AppTooltip>
                  </span>
                )}
                span={2}
              >
                <WorkingDayDefaultsEditor
                  value={workingDays}
                  onChange={(nextWorkingDays) => {
                    setWorkingDays(nextWorkingDays);
                    setWorkingDaysConfigured(true);
                  }}
                />
              </AppField>
            </AppFieldGrid>
          ) : (
            <DetailGrid>
              <DetailItem label={t("team.columns.access_level")} value={formatAccessLevel(form.access_level)} />
              <DetailItem
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("availability.user_settings.mode_title")}
                    <AppTooltip content={t("availability.user_settings.mode_info")}>
                      <IconInfo aria-label={t("availability.user_settings.mode_info_aria")} />
                    </AppTooltip>
                  </span>
                )}
                value={formatAvailabilityMode(form.availability_mode)}
              />
              <DetailItem label={t("team.columns.in_training")} value={formatBoolean(form.in_training)} />
              <DetailItem label={t("team.columns.regular_user")} value={formatBoolean(form.regular_user)} />
              <DetailItem label={t("team.working_days_standard")} value={workingDaysLabel} />
            </DetailGrid>
          )}
        </SectionCard>

        <SectionCard
          title={t("team.edit_employment") || "Beschäftigung"}
          icon={<WorkOutlineOutlinedIcon />}
          editing={editingCard === "employment"}
          editDisabled={anyCardEditing}
          onEdit={() => startEditing("employment")}
          onCancel={cancelEditing}
          onSave={() => handleSaveCard("employment")}
          saving={isSaving && editingCard === "employment"}
          dirty={isCardDirty("employment")}
        >
          {editingCard === "employment" ? (
            <AppFieldGrid columns={2} size="md">
              <AppField
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.columns.employment")}
                    <AppTooltip content={t("registration.employment_info")}>
                      <IconInfo aria-label={t("registration.employment_info_aria")} />
                    </AppTooltip>
                  </span>
                )}
                required
              >
                <AppSelect value={form.employment} onChange={onChange("employment")} options={[
                  { value: "Perm", label: t("registration.full_time") },
                  { value: "Temp", label: t("registration.part_time") },
                ]} />
              </AppField>
              <AppField label={t("team.columns.employment_level")} required>
                <AppSelect value={form.employment_level_pct} onChange={onChange("employment_level_pct")} options={percentOptions.map((value) => ({ value, label: `${value}%` }))} />
              </AppField>
              <AppField label={t("team.columns.hourly_wage")}>
                <AppInput
                  value={form.hourly_wage}
                  onChange={onHourlyWageChange}
                  inputMode="decimal"
                  pattern="[0-9]+([.,][0-9]{0,2})?"
                  trailingText="CHF"
                />
              </AppField>
              <AppField
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.columns.experience")}
                    <AppTooltip content={t("team.tooltips.experience")}>
                      <IconInfo aria-label={t("team.tooltips.experience_aria")} />
                    </AppTooltip>
                  </span>
                )}
              >
                <AppSelect
                  allowEmpty
                  emptyOptionLabel={t("none") || "Keine"}
                  value={form.experience}
                  onChange={onChange("experience")}
                  options={experienceOptions}
                />
              </AppField>
            </AppFieldGrid>
          ) : (
            <DetailGrid>
              <DetailItem
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.columns.employment")}
                    <AppTooltip content={t("registration.employment_info")}>
                      <IconInfo aria-label={t("registration.employment_info_aria")} />
                    </AppTooltip>
                  </span>
                )}
                value={formatEmployment(form.employment)}
              />
              <DetailItem label={t("team.columns.employment_level")} value={`${form.employment_level_pct || "-"}%`} />
              <DetailItem
                label={t("team.columns.hourly_wage")}
                value={form.hourly_wage === "" || form.hourly_wage == null ? "-" : `${form.hourly_wage} CHF`}
              />
              <DetailItem
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.columns.experience")}
                    <AppTooltip content={t("team.tooltips.experience")}>
                      <IconInfo aria-label={t("team.tooltips.experience_aria")} />
                    </AppTooltip>
                  </span>
                )}
                value={formatExperience(form.experience)}
              />
            </DetailGrid>
          )}
        </SectionCard>

        <SectionCard
          title={t("team.edit_assignments") || "Standort und Abteilungen"}
          icon={<BadgeOutlinedIcon />}
          editing={editingCard === "assignments"}
          editDisabled={anyCardEditing}
          onEdit={() => startEditing("assignments")}
          onCancel={cancelEditing}
          onSave={() => handleSaveCard("assignments")}
          saving={isSaving && editingCard === "assignments"}
          dirty={isCardDirty("assignments")}
        >
          {editingCard === "assignments" ? (
            <div className="team-edit-assignments">
              <AppFieldGrid columns={2} size="md">
                {locations.length > 1 && (
                  <AppField label={t("company.locations") || "Standorte"} required>
                    <AppSelect
                      multiple
                      value={selectedLocationIds}
                      disabled={!canManageUserLocations}
                      onChange={(event) => {
                        const value = event.target.value.map(Number);
                        setSelectedLocationIds(value);
                        const selectedSet = new Set(value);
                        setSelectedDepartmentIds((prev) =>
                          prev.filter((departmentId) => {
                            const department = departmentOptions.find(
                              (item) => Number(item.id) === Number(departmentId)
                            );
                            return (
                              selectedSet.size === 0 ||
                              !department ||
                              selectedSet.has(Number(department.location_id))
                            );
                          })
                        );
                      }}
                      options={locations.map((location) => ({ value: Number(location.id), label: location.name }))}
                    />
                  </AppField>
                )}

                {locations.length > 1 && (
                  <AppField
                    label={t("team.primary_location") || "Standard-Standort"}
                    help={t("team.primary_location_help")
                      || "Einstiegsstandort in Web und App."}
                  >
                    <AppSelect
                      value={effectivePrimaryLocationId}
                      disabled={!canManageUserLocations || primaryLocationOptions.length === 0}
                      placeholder={t("team.primary_location_placeholder") || "Standort auswählen"}
                      onChange={(event) => setPrimaryLocationId(Number(event.target.value))}
                      options={primaryLocationOptions}
                    />
                  </AppField>
                )}

                <AppField
                  label={t("team.columns.department")}
                  required
                  span={2}
                >
                  <AppSelect
                    multiple
                    searchable
                    value={selectedDepartmentIds}
                    onChange={(event) => setSelectedDepartmentIds(event.target.value.map(Number))}
                    options={departmentSelectOptions}
                  />
                </AppField>
              </AppFieldGrid>

              {selectedDepartments.length > 0 ? (
                <section className="team-edit-preferences">
                  <header className="team-edit-preferences__header">
                    <h3 className="team-edit-preferences__title">{t("team.department_preference")}</h3>
                    <p className="team-edit-preferences__subtitle">
                      {t("team.tooltips.department_preference")}
                    </p>
                  </header>
                  <div className="team-edit-preferences__list">
                    {selectedDepartments.map((department) => (
                      <div className="team-edit-preferences__row" key={department.id}>
                        <div className="team-edit-preferences__row-copy">
                          <strong>{department.name}</strong>
                          {activeLocationIds.length > 1 && departmentLocationName(department) ? (
                            <span>{departmentLocationName(department)}</span>
                          ) : null}
                        </div>
                        <AppSelect
                          value={rankingByDepartmentId[String(department.id)] ?? ""}
                          aria-label={`${t("team.department_preference")} · ${department.name}`}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setRankingByDepartmentId((current) => ({
                              ...current,
                              [String(department.id)]: nextValue === "" ? "" : Number(nextValue),
                            }));
                          }}
                          options={departmentPreferenceOptions}
                          placeholder={t("team.department_preference_placeholder")}
                          allowEmpty
                          emptyOptionLabel={t("team.department_preference_undefined")}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          ) : (
            <DetailGrid>
              {locations.length > 1 && (
                <DetailItem label={t("company.locations") || "Standorte"} value={selectedLocationNames} />
              )}
              {locations.length > 1 && (
                <DetailItem
                  label={t("team.primary_location") || "Standard-Standort"}
                  value={primaryLocationName}
                />
              )}
              <DetailItem label={t("team.columns.department")} value={selectedDepartmentSummary} />
            </DetailGrid>
          )}
        </SectionCard>

        <SectionCard
          title={t("team.edit_collaboration") || "Zusammenarbeit"}
          icon={<ManageAccountsOutlinedIcon />}
          editing={editingCard === "collaboration"}
          editDisabled={anyCardEditing}
          onEdit={() => startEditing("collaboration")}
          onCancel={cancelEditing}
          onSave={() => handleSaveCard("collaboration")}
          saving={isSaving && editingCard === "collaboration"}
          dirty={isCardDirty("collaboration")}
        >
          {editingCard === "collaboration" ? (
            <AppFieldGrid columns={1} size="md">
              <AppField
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.columns.preferred_ee") || "Bevorzugte Mitarbeitende"}
                    <AppTooltip content={t("team.tooltips.preferred_employees")}>
                      <IconInfo aria-label={t("team.tooltips.preferred_employees_aria")} />
                    </AppTooltip>
                  </span>
                )}
              >
                  <AppSelect
                    multiple
                    searchable
                    value={preferredEmployeeIds}
                    onChange={(event) => {
                      const value = event.target.value.map(Number);
                      setPreferredEmployeeIds(value);
                      setAvoidableEmployeeIds((prev) =>
                        prev.filter((employeeId) => !value.includes(Number(employeeId)))
                      );
                    }}
                    options={employeeOptions.map((employee) => ({
                      value: Number(employee.id),
                      label: getUserLabel(employee),
                      displayLabel: getUserLabel(employee),
                    }))}
                  />
              </AppField>

              <AppField
                label={(
                  <span className="team-edit-field-label-with-info">
                    {t("team.columns.avoidable_ee") || "Nicht gemeinsam einteilen"}
                    <AppTooltip content={t("team.tooltips.avoidable_employees")}>
                      <IconInfo aria-label={t("team.tooltips.avoidable_employees_aria")} />
                    </AppTooltip>
                  </span>
                )}
              >
                  <AppSelect
                    multiple
                    searchable
                    value={avoidableEmployeeIds}
                    onChange={(event) => {
                      const value = event.target.value.map(Number);
                      setAvoidableEmployeeIds(value);
                      setPreferredEmployeeIds((prev) =>
                        prev.filter((employeeId) => !value.includes(Number(employeeId)))
                      );
                    }}
                    options={employeeOptions.map((employee) => ({
                      value: Number(employee.id),
                      label: getUserLabel(employee),
                      displayLabel: getUserLabel(employee),
                    }))}
                  />
              </AppField>
            </AppFieldGrid>
          ) : (
            <DetailGrid>
              <DetailItem label={t("team.columns.preferred_ee") || "Bevorzugte Mitarbeitende"} value={preferredEmployeeNames} />
              <DetailItem label={t("team.columns.avoidable_ee") || "Nicht gemeinsam einteilen"} value={avoidableEmployeeNames} />
            </DetailGrid>
          )}
        </SectionCard>
        </div>
        )}
      </AppWorkspace>
      <ExitShiftsDialog
        open={exitDialog.open}
        userName={[form.first_name, form.last_name].filter(Boolean).join(" ")}
        exitDate={exitDialog.payload?.exit_date || ""}
        impact={exitDialog.impact}
        saving={isSaving}
        onCancel={closeExitDialog}
        onConfirm={handleExitDialogConfirm}
      />
    </div>
  );
};

export default EditUser;
